import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { useLanguage } from '../context/LanguageContext';
import { Calendar, Building, Layers } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

export default function Portfolio() {
  const { t, language } = useLanguage();
  const [portfolios, setPortfolios] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPortfolio, setSelectedPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPortfolios();
  }, []);

  const fetchPortfolios = async () => {
    try {
      const response = await axios.get(`${API}/portfolio.php`);
      setPortfolios(response.data);
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = ['all', ...new Set(portfolios.map(p => p.category))];

  const filteredPortfolios = selectedCategory === 'all' 
    ? portfolios 
    : portfolios.filter(p => p.category === selectedCategory);

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div data-testid="portfolio-page" className="circuit-bg">
      {/* Hero Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto mb-12"
          >
            <span className="text-primary font-medium mb-2 block">{t('portfolio.title')}</span>
            <h1 className="text-4xl sm:text-5xl font-bold mb-6">{t('portfolio.subtitle')}</h1>
          </motion.div>

          {/* Category Filter */}
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap justify-center gap-2 mb-12"
          >
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category)}
                data-testid={`filter-${category}`}
                className="rounded-full"
              >
                {category === 'all' ? t('portfolio.all') : category}
              </Button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <div className="h-48 bg-muted" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded mb-2" />
                    <div className="h-4 bg-muted rounded w-3/4" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : filteredPortfolios.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {language === 'id' ? 'Belum ada portofolio' : 'No portfolios yet'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPortfolios.map((portfolio, index) => (
                <motion.div
                  key={portfolio.id}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card 
                    data-testid={`portfolio-card-${portfolio.id}`}
                    className="group h-full border border-border hover:border-primary/50 transition-all duration-300 overflow-hidden cursor-pointer"
                    onClick={() => setSelectedPortfolio(portfolio)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={portfolio.image_url}
                        alt={language === 'id' ? portfolio.title_id : portfolio.title_en}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-card to-transparent" />
                      <Badge className="absolute top-4 left-4 bg-primary/90">
                        {portfolio.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg mb-2 line-clamp-1">
                        {language === 'id' ? portfolio.title_id : portfolio.title_en}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                        {language === 'id' ? portfolio.description_id : portfolio.description_en}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Building className="h-4 w-4" />
                          {portfolio.client}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {portfolio.year}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Portfolio Detail Dialog */}
      <Dialog open={!!selectedPortfolio} onOpenChange={() => setSelectedPortfolio(null)}>
        <DialogContent className="max-w-2xl" data-testid="portfolio-dialog">
          {selectedPortfolio && (
            <>
              <div className="relative h-64 -mx-6 -mt-6 mb-4 overflow-hidden rounded-t-lg">
                <img 
                  src={selectedPortfolio.image_url}
                  alt={language === 'id' ? selectedPortfolio.title_id : selectedPortfolio.title_en}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background to-transparent" />
              </div>
              <DialogHeader>
                <Badge className="w-fit mb-2">{selectedPortfolio.category}</Badge>
                <DialogTitle className="text-2xl">
                  {language === 'id' ? selectedPortfolio.title_id : selectedPortfolio.title_en}
                </DialogTitle>
                <DialogDescription className="text-base">
                  {language === 'id' ? selectedPortfolio.description_id : selectedPortfolio.description_en}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-primary" />
                  <span className="text-sm">
                    <strong>{t('portfolio.client')}:</strong> {selectedPortfolio.client}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span className="text-sm">
                    <strong>{t('portfolio.year')}:</strong> {selectedPortfolio.year}
                  </span>
                </div>
                <div className="flex items-start gap-2">
                  <Layers className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <div>
                    <strong className="text-sm">{t('portfolio.technologies')}:</strong>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedPortfolio.technologies.map((tech) => (
                        <Badge key={tech} variant="secondary">{tech}</Badge>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
